import { ee } from "../../services/earth-engine";
import { computeBearing, computeDisplacement } from "../geodesy";
import * as Metadata from "../../common/metadata";
import { getDate } from "../utils";
import { callback, evaluate } from "../../store/tools/effects";
import { ContactSupportOutlined } from "@material-ui/icons";

//src\algorithms\shoreline\extraction.js

/**
 * Partitions the raster *image* using the water index
 * and the threshold function, then reduces the output
 * raster to vector, corresponding to the water body in
 * the given *image*.
 * @param {ee.Image} image
 * @param {ee.Geometry} geometry
 * @param {Number} scale
 * @param {Object} bands
 * @param {Function} thresholdFn Threshold supplier function
 * @returns {ee.Geometry} the identified water body
 */
export const identifyWaterFeature = (
  image,
  geometry,
  scale,
  bands,
  thresholdFn
) => {
  //console.log("NDWI -init");
  const internalBandName = "NDWI";

  /* Produce water index */
 // let imgInfo = image.getInfo();
  //console.log("Image (NDWI)", image, imgInfo,[bands.green, bands.nir], internalBandName);
  const ndwi = ee
    .Image(image)
    .normalizedDifference([bands.green, bands.nir])
    .rename(internalBandName);
  //console.log("NDWI- Value","ndwi",ndwi,ndwi.getInfo());
  //console.log("threshouldFn","geometry", geometry, "scale", scale);
  let threshold = ee.Number(0.01); 
  //console.log("NDWI - threshold default",threshold.getInfo());
  try{
    //console.log("before thresholdFn", "parameter ndwi/histogram", ndwi,ndwi.getInfo());
    //console.log("function - thresholdFn", thresholdFn);
    threshold = thresholdFn(ndwi, internalBandName, geometry, scale);
   // console.log("after thresholdFn");
  }catch(e){
    console.log("error calculating  threshold - using value",threshold);
  }
 // console.log("NDWI - threshold",threshold.getInfo());
/*
 let func = function* () {
    let resultThresholdNumber = ee.Number(threshold);
    console.log("threshold ee.Number", resultThresholdNumber.getInfo(), resultThresholdNumber);
    let resultThreshold = yield evaluate(ee.Number(threshold));
    console.log("RESULT-Threshold-evaluate", resultThreshold );
    console.log("Threshold-TEXT", JSON.stringify(resultThreshold));
  }
  let x = func();
  console.log("RESULT-Threshold-evaluate-yield-result",x);
  console.log("RESULT-Threshold-evaluate-yield-result-next-value",x.next().value);
  */
  /**
   * Partitions image and reduces to a single vector
   */
  //console.log("NDWI - Water",ndwi);
  const water = ee.Feature(
    ndwi
      .clip(geometry)
      .gt(threshold)
      .reduceToVectors({ scale, maxPixels: 1e12 })
      .filter(ee.Filter.eq("label", 1))
      .limit(1, "count", false)
      .first()
  );
  //console.log("NDWI - Water -v2",water, water.getInfo());
  let waterLocal = water.getInfo();
  let result = null;
  if(waterLocal  !== null){ 
    result = water.geometry();
    //console.log("NDWI","geometry-water",result, result.getInfo()); 
  }else{
    console.log("water is null (lower than threshold) - image excluded by threshold");
    
  }
  return result;
};

/**
 * Removes noises from the shoreline by keeping only
 * the polygons that have the most intersections with
 * the transects
 * @param {ee.Geometry.MultiLineString} shoreline
 * @param {ee.Geometry.MultiLineString|ee.FeatureCollection} transects
 * @returns {ee.Feature<ee.Geometry.LineString>} shoreline without noise
 */
export const removeShorelineNoise = (shorelines, transects) => {
  const coordinates = ee.Geometry(shorelines).coordinates();

  const guard = ee.List(
    ee.Algorithms.If(
      ee.Geometry(shorelines).type().compareTo("MultiLineString").eq(0),
      coordinates,
      [coordinates]
    )
  );

  const weighted = guard.map((segment) => {
    const linestring = ee.Geometry.LineString(segment);
    return ee.Feature(linestring, {
      weight: linestring.intersection(transects).coordinates().size(),
    });
  });

  return ee
    .Feature(ee.FeatureCollection(weighted).limit(1, "weight", false).first())
    .geometry();
};

/**
 * Creates a gaussian kernel controlled by *size*, *mean*
 * and *sigma*. The output kernel size is always odd.
 * @param {Number} size
 * @param {Number} mean
 * @param {Number} sigma
 * @returns {ee.List<Number>} the gaussian kernel
 */
export const gaussianKernel = (size, mean, sigma) => {
  const gaussianCurve = (x, mean, sigma) => {
    const divider = ee
      .Number(sigma)
      .multiply(ee.Number(2).multiply(Math.PI).sqrt());
    const exponent = ee.Number(-1).multiply(
      ee
        .Number(x)
        .subtract(mean)
        .pow(2)
        .divide(ee.Number(2).multiply(ee.Number(sigma).pow(2)))
    );

    return ee.Number(1).divide(divider).multiply(exponent.exp());
  };

  const half = ee.Number(size).divide(2).floor();

  const begin = ee.Number(0).subtract(half),
    end = ee.Number(0).add(half);

  // Get the normal distribution Y value for each X
  // in the interval
  const kernel = ee.List.sequence(begin, end).map((i) =>
    gaussianCurve(i, mean, sigma)
  );

  const sum = kernel.reduce(ee.Reducer.sum());

  // Normalize each value, so that the sum of the list
  // will be equal to one
  const normalizedKernel = kernel.map((val) => ee.Number(val).divide(sum));

  return normalizedKernel;
};

/**
 * Convolves over the list *coordinates* producing local averages
 * weighted by the produced kernel of *samples*, *mean*, *sd*.
 * The result is a smoothed list (often linestring) with removed noises
 * such as pixel-induced staircase effect.
 * @param {ee.List} coordinates List containing the path
 * @param {ee.Number} samples Size of the kernel
 * @param {ee.Number} mean Center of the normal distribution
 * @param {ee.Number} sd Standard Deviation, controls the size of the bell-shaped curve
 * @returns {ee.List<Number>} the filtered coordinates
 */
export const linearGaussianFilter = (
  coordinates,
  samples = 3,
  mean = 0,
  sd = 1
) => {
  const coordinateList = ee.List(coordinates);

  // Setup gauss distribution kernel parameters
  const kernelSize = ee.Number(samples);
  const kernelMean = ee.Number(mean);
  const kernelSd = ee.Number(sd);
  const kernel = gaussianKernel(kernelSize, kernelMean, kernelSd);

  const first = coordinateList.reduce(ee.Reducer.first()),
    last = coordinateList.reduce(ee.Reducer.last());

  const sequence = ee.List.sequence(
    ee.Number(0),
    coordinateList.length().subtract(kernelSize)
  );

  const path = sequence.map((index) => {
    // Take interval of the kernel size to apply the smoothing
    // and zip it to the kernel, so each element in the new list
    // will be a pair of a 2d point and its weight
    const interval = coordinateList
      .slice(ee.Number(index), ee.Number(index).add(kernelSize))
      .zip(kernel);

    // Map the elements, multiplying their axis values by their weight
    const gaussian = interval.map((element) => {
      // Each element contains a 2d point (0) and a kernel weight (1)
      const asList = ee.List(element);

      const point = ee.List(asList.get(0));
      const weight = ee.Number(asList.get(1));

      // Now we map the two values (each point dimention), multiplying to the weight
      return point.map((value) => ee.Number(value).multiply(weight));
    });

    // Sum longitude and latitude separately
    const smoothenLong = gaussian
      .map((point) => ee.List(point).get(0))
      .reduce(ee.Reducer.sum());
    const smoothenLat = gaussian
      .map((point) => ee.List(point).get(1))
      .reduce(ee.Reducer.sum());

    // Return final smoothen point
    return ee.List([smoothenLong, smoothenLat]);
  });

  const smoothen = ee.List([]).add(first).cat(path).add(last);

  // return original coordinates if the kernelSize is less than or equal to the length
  // of the given coordinates, otherwise return smoothen coordinates.
  return ee.Algorithms.If(
    coordinateList.size().lte(kernelSize),
    coordinateList,
    smoothen
  );
};

/**
 * Given a histogram, find the appropriate thresholds for *count* classes
 * @param {ee.Dictionary} histogram
 * @param {Number} count
 * @returns {ee.Number} the optimal threshold
 */
export const thresholdingAlgorithm = (histogram, count) => {
  //console.log("histogram",histogram.getInfo());
  console.log("histogram(before ee.Dictionary)", histogram, histogram.getInfo());
  let eeHistogram= ee.Dictionary(histogram);
  console.log("eeHistogram(after ee.Dictionary)",eeHistogram.getInfo());
  let eeHistogramInfo = eeHistogram.getInfo();
  if(typeof(eeHistogramInfo.bucketMeans) === "undefined"){
    console.warn("Error calculating thresholdingAlgorithm");
    throw new Error("Error calculating thresholdingAlgorithm")
  };
  const counts = ee.Array(ee.Dictionary(histogram).get("histogram"));
  let bucketMeans = ee.Dictionary(histogram).get("bucketMeans");
  //console.log("bucketMeans", bucketMeans);

 
  const means = ee.Array(bucketMeans);
  //console.log("bucketMeans",means);
  //console.log("counts",counts);
  const size = means.length().get([0]);
  //console.log("size",size);
  const total = counts.reduce(ee.Reducer.sum(), [0]).get([0]);
  //console.log("total",total);
  const sum = means.multiply(counts).reduce(ee.Reducer.sum(), [0]).get([0]);
  //console.log("sum",sum);
  const mean = sum.divide(total);
  //console.log("mean",mean);
  const bss = ee.List.sequence(1, size).map((i) => {
    const aCounts = counts.slice(0, 0, i);
    const aCount = aCounts.reduce(ee.Reducer.sum(), [0]).get([0]);
    const aMeans = means.slice(0, 0, i);
    const aMean = aMeans
      .multiply(aCounts)
      .reduce(ee.Reducer.sum(), [0])
      .get([0])
      .divide(aCount);
    const bCount = total.subtract(aCount);
    const bMean = sum.subtract(aCount.multiply(aMean)).divide(bCount);
    return aCount
      .multiply(aMean.subtract(mean).pow(2))
      .add(bCount.multiply(bMean.subtract(mean).pow(2)));
  });

  return means.sort(bss).get([-1]);
};

/**
 * Selects the threshold function according to
 * the input *threshold*.
 * This will be refactored to use something other than
 * a number to choose the intended threshold function.
 * @param {Number} threshold
 * @returns {Function} the thresholding function
 */
const selectThreshold = (threshold) => {
  try{
   // console.log("NDWI-histogram:", ee.Reducer.histogram().getInfo());
  }catch(e){

  }
  /*
  console.log( "ee.Reducer.histogram()-extraction.js-L276",  image.reduceRegion({
              reducer: ee.Reducer.histogram(),
              scale,
              maxPixels: 1e12,
            }).get(band).getInfo());
            */
  //console.log("ee.Reducer.histogram()",ee.Reducer.histogram().getInfo());
  const maxPixels = 1e12;
  switch (threshold) {
    case 0.0:
      return (image, band, _, scale) => {
        //landsat
        scale = 30;
        //console.log("DEBUG:","image",image,image.getInfo(),"scale",scale, "band", band, "maxPixel", maxPixels);
        
        let min = image.reduceRegion({
              reducer: ee.Reducer.min(),
              scale,
              bestEffort: true
            }).get(band);
        
        console.log("MIN:", min, min.getInfo());
        let returnValue = thresholdingAlgorithm(
          image.reduceRegion({
              reducer: ee.Reducer.histogram(),
              scale,
              //maxPixels: maxPixels,
              bestEffort: true
            })
            .get(band),
          2
        );
        return returnValue
          }
    case -1.0:
      return (image, band, _, scale) => {
        scale = 300;
        //console.log("DEBUG:","image",image,image.getInfo(),"scale",scale, "band", band, "maxPixel", maxPixels);
          let returnValue = thresholdingAlgorithm(
            image
              .reduceRegion({
                reducer: ee.Reducer.histogram(),
                scale,
                maxPixels: maxPixels,
              })
              .get(band),
            3
          );
          return  returnValue;
      }
    default:
      return () => threshold;
  }
};

/**
 * Extracts the raw shoreline of the given *image*,
 * by identifying the water feature (body) and
 * converting it into linestrings
 * @param {ee.Image} image
 * @param {ee.Geometry} geometry
 * @param {Number} scale
 * @param {Object} bands
 * @param {Function} thresholdFn
 * @returns {ee.Geometry.MultiLineString}
 */
export const extractShoreline = (
  image,
  geometry,
  scale,
  bands,
  thresholdFn
) => {
  //console.log("",thresholdFn);
  const waterSegment = identifyWaterFeature(
    image,
    geometry,
    scale,
    bands,
    thresholdFn
  );
  //console.log("waterSegment 2:",waterSegment);
  if(waterSegment === null ) return null;
  return ee.Geometry.MultiLineString(waterSegment.coordinates().get(0)).intersection(
    geometry.buffer(-10)
  );
};

/**
 * Fully derives the shoreline using the satellite *image*.
 * The satellite derived shoreline is a single smoothed linestring
 * without noisy features (polygons that are not the shoreline).
 * @param {ee.Image} image
 * @param {ee.Geometry} geometry
 * @param {Number} scale
 * @param {Object} bands
 * @param {Function} threshold
 * @param {ee.FeatureCollection|ee.List} transects
 * @param {Array<String>} props
 */

let firstImage = null;
export const deriveShoreline = (
  image,
  geometry,
  scale,
  bands,
  threshold,
  transects,
  props,
  opticalResolution
) => {
 // console.log("deriveShoreline: A");
 //console.log("deriveShoreline 1", "transects", transects);
  const transectsGeometry = ee.FeatureCollection(transects).geometry();
 // console.log("deriveShoreline: B");
  /*
  evaluate(ee.List(transects)).then ( (result, error) => {

  });
  */
//apply register

if(firstImage === null){
  firstImage = image;
  firstImage = firstImage.select(bands.red, bands.green, bands.blue, bands.nir);
  //console.log("Register", firstImage.getInfo());
}else{
  let maxOffset = opticalResolution;
  //console.log("Register", "calculating register....", bands);
  image = image.select(bands.red, bands.green, bands.blue, bands.nir);
  image = image.register(firstImage, maxOffset);
  //console.log("Register apply with maxOffset", maxOffset);
}


  //console.log("deriveShoreline 1.1","deriveShoreline","transectsGeometry",transectsGeometry);
  const shoreline = extractShoreline(
    image,
    geometry,
    scale,
    bands,
    selectThreshold(threshold)
  );
  if(shoreline === null){
    let imgInfo = image.getInfo();
    let errors = JSON.parse(window.sessionStorage.getItem("shorelineImagesErrors"));
    let dtTimeStamp = imgInfo.properties["system:time_start"].value;
    errors.push({message:"Image excluded by threshold", date: dtTimeStamp, "dateFormated": new Date(dtTimeStamp).toISOString()});
    window.sessionStorage.setItem("shorelineImagesErrors",JSON.stringify(errors)); 
    console.error("shoreline with problem (threshold): returned null", imgInfo );//blank or not readable images
    return null;
  } 
 // console.log("deriveShoreline: C");
 // console.log("deriveShoreline 1.2","deriveShoreline-transectsGeometry-shoreline",shoreline);
  const enhanced = removeShorelineNoise(shoreline, transectsGeometry);
 // console.log("deriveShoreline 1.3","deriveShoreline: D");
  const smoothen = linearGaussianFilter(enhanced.coordinates());//enhanced.coordinates(): partial results?
 // console.log("deriveShoreline 1.4","smoothen", smoothen);
  let result = ee.Feature(ee.Geometry.LineString(smoothen), {
    // Format Data as ISO standart formating and and UTC
    [Metadata.TIME_START]: ee.Date(getDate(image)).format(null, 'UTC'),
  });
 //console.log("deriveShoreline 1.5","deriveShoreline: success",shoreline);
  return result;
};

/**
 * Acquires and extracts shorelines given an array of dates
 * @param {Array} dates a list containing the dates of the images to be analysed
 * @param {Object} satellite information about the satellite
 * @param {ee.Geometry} geometry the Area of Interest of the this  session
 * @param {ee.Number} threshold Suggested threshold for the extraction
 * @returns {ee.FeatureCollection} a FeatureCollection of polygons
 */
export const deriveShorelines = (
  dates,
  satellite,
  geometry,
  scale,
  threshold,
  transects
) => {
  //console.log("deriveShorelines", "dates", dates,"satellite",satellite, "geometry", geometry, "scale", scale,"threshold",threshold,"transects",transects);
  const data = dates.map((value) => {
    const mission = satellite.missions.find(
      (mission) => mission.name === value.name
    );
    //console.log("Bands:",mission,mission.bands);
    try{
      return deriveShoreline(
        ee.Image(mission.algorithms.acquire(value.date, geometry)),//substituir para imagens já selecionadas
        geometry,
        scale,
        mission.bands,
        threshold,
        transects,
        null,
        mission.opticalResolution
      );
    }catch(e){
      console.error("deriveShorelines  - Error on (ignoring image)", value, "returning null", e);
      return null;
    }
  });

  return data;
};

/**
 * Expands the given *coordinates* by an amount *distance*
 * @param {} coordinates
 * @param {} distance
 */
export const expandCoastline = (coordinates, distance) => {
  coordinates = ee.List(coordinates);
  const neighbors = ee.List(coordinates.slice(1));

  let displacedSegments = coordinates.zip(neighbors).map((segment) => {
    segment = ee.List(segment);
    const current = ee.List(segment.get(0));
    const neighbor = ee.List(segment.get(1));
    const bearing = computeBearing(
      current.get(0),
      current.get(1),
      neighbor.get(0),
      neighbor.get(1)
    );

    const p1 = computeDisplacement(
      current.get(0),
      current.get(1),
      bearing.add(Math.PI * 0.5),
      distance
    );
    const p2 = computeDisplacement(
      neighbor.get(0),
      neighbor.get(1),
      bearing.add(Math.PI * 0.5),
      distance
    );
    return ee.List([p1, p2]);
  });

  displacedSegments = rectifyJunctions(displacedSegments, coordinates);
  const combined = coordinates.reverse().splice(0, 0, displacedSegments);

  return ee.Feature(ee.Geometry.Polygon([combined]));
};

/**
 * Normalizes the junctions *segments*
 * @param {} segments
 * @param {} originalCoordinates
 */
const rectifyJunctions = (segments, originalCoordinates) => {
  segments = ee.List(segments);
  originalCoordinates = ee.List(originalCoordinates).slice(1);
  const neighbors = ee.List(segments.slice(1));

  let processed = ee.List.sequence({ start: 0, count: neighbors.size() }).map(
    (index) => {
      const currentSegment = ee.List(segments.get(index));
      const neighborSegment = ee.List(neighbors.get(index));
      const original = ee.List(originalCoordinates.get(index));

      const previous = ee.List(currentSegment.get(1));
      const next = ee.List(neighborSegment.get(0));

      const alpha = computeBearing(
        original.get(0),
        original.get(1),
        previous.get(0),
        previous.get(1)
      );
      const beta = computeBearing(
        original.get(0),
        original.get(1),
        next.get(0),
        next.get(1)
      );
      const difference = alpha.subtract(beta); // Angle between previous and next
      const halfDifference = difference.divide(2);
      const theta = beta.add(halfDifference);

      const adjacent = ee.Geometry.Point(original).distance(
        ee.Geometry.Point(next)
      );
      const length = ee.Number(adjacent).divide(halfDifference.cos()); // cosθ = adjacent / hypotenuse

      const centroid = computeDisplacement(
        original.get(0),
        original.get(1),
        theta,
        length
      );

      return centroid;
    }
  );

  const first = ee.List(ee.List(segments.get(0)).get(0)); // Start of the first segment
  const last = ee.List(ee.List(segments.get(-1)).get(1)); // End of the last segment

  processed = ee.List(processed);
  processed = processed.insert(0, ee.List([first.get(0), first.get(1)]));
  processed = processed.add(ee.List([last.get(0), last.get(1)]));

  return processed;
};
